import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { formatCurrency, useAsyncEffect } from '@recargas-dominicanas/core/utils';
import { useBusinessStore } from '../../stores/businessStore';
import { CustomerCard } from '../../components/customer-card/customer-card.component';
import { Transactions } from './transactions/transactions.component';
import { Products } from './products/products.component';
import { Users } from './users/users.component';
import { Balances } from './balances/balances.component';
import { OptionButton } from '../../components/option-button/option-button.component';
import { AddBalanceModal } from './add-balance-modal/add-balance-modal.component';
import { ConfirmModal } from '../../components/confirm-modal/confirm-modal.component';
import { BusinessApi } from '../../api/business.api';
import { useSalesStore } from '../../stores/salesStore';

import { 
  Case,
  OutlineCard,
  PageContainer, 
  PageContent, 
  PageToolbar,
  Switch, 
  Tab, 
  TabBar, 
  ToolbarTitle 
} from '@recargas-dominicanas/core/components';

import { style } from './business.module.css';
import { Sales } from './sales/sales.component';

export function Business() {

  const params = useParams<{ id: string }>();
  const businessId = parseInt(params.id!);
  const businessStore = useBusinessStore();
  const salesStore = useSalesStore();

  const [openAddBalanceModal, setOpenAddBalanceModal] = useState(false);
  const [balance, setBalance] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState('sales');

  useEffect(() => {
    return () => businessStore.reset();
  }, []);

  useAsyncEffect(async () => {

    if (!businessStore.business) {
      await businessStore.fetchById(businessId);
    }

    await businessStore.fetchProducts(businessId);
    await businessStore.fetchTransactions(businessId, 1, 25);
    await businessStore.fetchSalesReports(businessId, 1, 25);
    await businessStore.fetchBalances(businessId, 1, 25);
    await businessStore.fetchUsers(businessId);
    await businessStore.fetchSales(businessId);
  });

  function handleTabClick(tab: any) {
    setSelectedTab(tab.tag);
  }

  async function handleAddBalance() {

    if (!balance) return;

    setError(false);

    try {

      setLoading(true);

      await BusinessApi.addBalance(businessId, balance);

      await businessStore.fetchById(businessId);
      await businessStore.fetchBalances(businessId, 1, 25);
      await salesStore.fetchBalance();

      setLoading(false);
      setOpenConfirmModal(false);

    } catch {

      setLoading(false);
      setError(true);
    }
  }

  function handleOpenConfirmModal(balance: number) {
    setError(false);
    setBalance(balance);
    setOpenConfirmModal(true);
    setOpenAddBalanceModal(false);
  }

  if (!businessStore.business) return null;

  return (
    <PageContainer className={style.container}>
      <PageToolbar className={style.toolbar}>
        <ToolbarTitle 
          style={style.title}
          title={businessStore.business.name}/>
        <div className={style.toolbarRight}>
          <TabBar onTabClick={handleTabClick}>
            <Tab style={style.tab} tag='sales' title='Ventas'/>
            <Tab style={style.tab} tag='transactions' title='Transacciones'/>
            <Tab style={style.tab} tag='products' title='Productos'/>
            <Tab style={style.tab} tag='balances' title='Balances'/>
            <Tab style={style.tab} tag='users' title='Usuarios'/>
          </TabBar>
          <OptionButton
            icon='account_balance_wallet'
            title='Asignar balance'
            onClick={() => setOpenAddBalanceModal(true)}/>
        </div>
      </PageToolbar>
      <PageContent className={style.content}>
        <CustomerCard
          style={style.customerCard}
          business={businessStore.business}/>
        <OutlineCard className={style.outlineCard}>
          <Switch caseId={selectedTab}>
            <Case caseId='sales'><Sales/></Case>
            <Case caseId='transactions'><Transactions/></Case>
            <Case caseId='products'><Products/></Case>
            <Case caseId='balances'><Balances/></Case>
            <Case caseId='users'><Users/></Case>
          </Switch>
        </OutlineCard>
      </PageContent>
      <AddBalanceModal
        open={openAddBalanceModal}
        onClose={() => setOpenAddBalanceModal(false)}
        onSave={handleOpenConfirmModal}/>
      {balance &&
        <ConfirmModal
          style={style.confirmModal}
          open={openConfirmModal}
          title='Asignar balance'
          disableButtons={loading}
          autoClose={false}
          disableConfirmation={true}
          confirmButtonText='Confirmar'
          onConfirm={handleAddBalance}
          onClose={() => setOpenConfirmModal(false)}>
          Esta seguro de querer asignar <b>{formatCurrency(balance)}</b> al negocio <b>{businessStore.business.name} (ID {businessId})</b>?
          {error &&
            <div className={style.error}>
              Ocurrio un error al asignar el balance.
            </div>
          }
        </ConfirmModal>
      }
    </PageContainer>
  );
}
